import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { cloudKitService } from '../services/cloudkit';
import { Share, ShareMessage } from '../types/Share';

export function useShares() {
  const query = useQuery<Share[], Error>({
    queryKey: ['shares'],
    queryFn: () => cloudKitService.fetchShares(),
    staleTime: Infinity, // Data won't become stale - memoized
    gcTime: Infinity,
  });

  const memoizedShares = useMemo(() => query.data || [], [query.data]);

  return {
    ...query,
    shares: memoizedShares,
  };
}

export function useShareMessages() {
  const query = useQuery<ShareMessage[], Error>({
    queryKey: ['shareMessages'],
    queryFn: () => cloudKitService.fetchShareMessages(),
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const memoizedMessages = useMemo(() => query.data || [], [query.data]);

  return {
    ...query,
    messages: memoizedMessages,
  };
}

export function useShareWithMessages(shareRecordName: string) {
  const { shares } = useShares();
  const { messages } = useShareMessages();

  const shareWithMessages = useMemo(() => {
    const share = shares.find(s => s.recordName === shareRecordName);
    const relatedMessages = messages.filter(m => m.shareParent === shareRecordName);
    return { share, messages: relatedMessages };
  }, [shares, messages, shareRecordName]);

  return shareWithMessages;
}
